/**
 * Impact by Product Widget
 * Bar chart matching the screenshot showing lots and wafers impacted
 */

interface ProductImpact {
  product: string
  lots: number
  wafers: number
}

interface ImpactByProductWidgetProps {
  data: ProductImpact[]
  usesCustomScope?: boolean
  isEditMode?: boolean
}

export function ImpactByProductWidget({ data, usesCustomScope, isEditMode }: ImpactByProductWidgetProps) {
  const maxWafers = Math.max(...data.map((d) => d.wafers))
  const maxLots = Math.max(...data.map((d) => d.lots))

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-lg border border-[#e6e6e6] bg-white">
      {usesCustomScope && !isEditMode && (
        <div className="absolute right-5 top-5 z-10">
          <div className="flex items-center rounded-3xl border border-[#ff8637] bg-[#fff7ed] px-2 py-1">
            <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#c83408]">
              Custom Scope
            </span>
          </div>
        </div>
      )}

      <div className="px-5 pt-5">
        <h3 className="text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]">Impact by Product</h3>
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5">
        {/* Legend - Above chart */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="h-[10px] w-[10px] rounded-full bg-[#ff8637]" />
            <span className="text-sm tracking-[0.14px] text-[#727272]">Lots</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-[10px] w-[10px] rounded-full bg-[#00afff]" />
            <span className="text-sm tracking-[0.14px] text-[#727272]">Wafers</span>
          </div>
        </div>

        {/* Vertical Grouped Bar Chart */}
        <div className="min-h-[240px] flex-1">
          <svg
            viewBox="0 0 800 300"
            className="h-full w-full"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Y-axis label */}
            <text
              x="15"
              y="15"
              fill="#727272"
              fontSize="12"
              fontWeight="400"
            >
              Count
            </text>

            {/* Y-axis grid lines and labels */}
            {[0, 200, 400, 600].map((value) => {
              const y = 250 - (value / 600) * 200
              return (
                <g key={value}>
                  <line
                    x1="60"
                    y1={y}
                    x2="750"
                    y2={y}
                    stroke="#e6e6e6"
                    strokeWidth="1"
                  />
                  <text
                    x="52"
                    y={y + 4}
                    textAnchor="end"
                    fill="#727272"
                    fontSize="11"
                  >
                    {value}
                  </text>
                </g>
              )
            })}

            {/* Bars - All 7 products */}
            {data.map((item, index) => {
              const barWidth = 32
              const groupWidth = 95
              const x = 80 + index * groupWidth
              const lotsHeight = Math.max((item.lots / maxLots) * 180, 3)
              const wafersHeight = Math.max((item.wafers / maxWafers) * 180, 3)

              return (
                <g key={item.product}>
                  {/* Lots bar (orange) */}
                  <rect
                    x={x}
                    y={250 - lotsHeight}
                    width={barWidth}
                    height={lotsHeight}
                    fill="#ff8637"
                    rx="2"
                  />

                  {/* Wafers bar (blue) */}
                  <rect
                    x={x + barWidth + 5}
                    y={250 - wafersHeight}
                    width={barWidth}
                    height={wafersHeight}
                    fill="#00afff"
                    rx="2"
                  />

                  {/* Product label (horizontal, full name) */}
                  <text
                    x={x + barWidth}
                    y="270"
                    transform={`rotate(-35 ${x + barWidth} 270)`}
                    fill="#727272"
                    fontSize="10"
                    textAnchor="end"
                  >
                    {item.product}
                  </text>
                </g>
              )
            })}

            {/* X-axis label */}
            <text
              x="400"
              y="295"
              fill="#727272"
              fontSize="12"
              fontWeight="400"
              textAnchor="middle"
            >
              Product
            </text>
          </svg>
        </div>
      </div>
    </div>
  )
}
